import JMuxerModule from "jmuxer/dist/jmuxer.min.js";
import { performanceStore } from "../performance/store";
import { directControl } from "./controlStore";
import type { DirectTouch } from "./protobuf";
import type { StreamProfile } from "./AndroidWebRtcClient";

interface Callbacks {
  onFrame: (frame: VideoFrame) => void;
  onState: (state: string) => void;
  onError: (error: Error) => void;
}

interface JMuxerFeed {
  video: Uint8Array;
  duration?: number;
  isLastVideoFrameComplete?: boolean;
}

interface JMuxerInstance {
  feed: (data: JMuxerFeed) => void;
  destroy: () => void;
}

interface JMuxerOptions {
  node: HTMLVideoElement;
  mode: "video";
  flushingTime: number;
  maxDelay: number;
  clearBuffer: boolean;
  fps: number;
  onReady: () => void;
  onError: (error: unknown) => void;
  onUnsupportedCodec: (codec: string) => void;
}

type JMuxerConstructor = new (options: JMuxerOptions) => JMuxerInstance;

const socketUrl = (path: string, profile: StreamProfile) =>
  `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}${path}?profile=${encodeURIComponent(profile)}`;

const concat = (left: Uint8Array, right: Uint8Array) => {
  const result = new Uint8Array(left.length + right.length);
  result.set(left);
  result.set(right, left.length);
  return result;
};

const avcCodecFromAnnexB = (config: Uint8Array) => {
  for (let index = 0; index <= config.length - 8; index += 1) {
    const fourByteStart = config[index] === 0 && config[index + 1] === 0 && config[index + 2] === 0 && config[index + 3] === 1;
    const threeByteStart = config[index] === 0 && config[index + 1] === 0 && config[index + 2] === 1;
    const nal = index + (fourByteStart ? 4 : threeByteStart ? 3 : 0);
    if (nal !== index && (config[nal] & 0x1f) === 7 && nal + 3 < config.length) {
      return `avc1.${[config[nal + 1], config[nal + 2], config[nal + 3]]
        .map((value) => value.toString(16).padStart(2, "0")).join("")}`;
    }
  }
  return "avc1.42C029";
};

const keyCodes: Record<string, number> = {
  GoHome: 3, GoBack: 4, Power: 26, AppSwitch: 187,
  AudioVolumeUp: 24, AudioVolumeDown: 25,
  Enter: 66, Backspace: 67, Delete: 112, Escape: 4, Tab: 61, " ": 62,
  ArrowUp: 19, ArrowDown: 20, ArrowLeft: 21, ArrowRight: 22,
};

const androidKeyCode = (key: string) => {
  if (keyCodes[key] !== undefined) return keyCodes[key];
  if (/^[a-z]$/i.test(key)) return 29 + key.toUpperCase().charCodeAt(0) - 65;
  if (/^[0-9]$/.test(key)) return 7 + Number(key);
  return undefined;
};

const setU16 = (view: DataView, offset: number, value: number) => view.setUint16(offset, value, false);
const setU32 = (view: DataView, offset: number, value: number) => view.setUint32(offset, value, false);

export class ScrcpyWebCodecsClient {
  static webCodecsSupported() {
    return "VideoDecoder" in window && "EncodedVideoChunk" in window;
  }

  static mseSupported() {
    return "MediaSource" in window;
  }

  static supported() {
    return this.webCodecsSupported() || this.mseSupported();
  }

  private videoSocket?: WebSocket;
  private controlSocket?: WebSocket;
  private decoder?: VideoDecoder;
  private muxer?: JMuxerInstance;
  private videoFrameCallbackId?: number;
  private lastPresentedFrames = 0;
  private lastMseTimestamp?: number;
  private msePlaying = false;
  private codecConfig?: Uint8Array;
  private decoderCodec = "avc1.42C029";
  private configured = false;
  private configurePromise?: Promise<void>;
  private pendingKeyFrame?: { timestamp: number; payload: Uint8Array };
  private awaitingKeyFrame = true;
  private stopped = false;
  private reconnectTimer?: number;
  private activeTouches = new Set<number>();
  private submitted = new Map<number, number>();
  private decodedFrames = 0;
  private droppedFrames = 0;
  private intervalFrames = 0;
  private intervalStarted = performance.now();
  private decodeTotalMs = 0;
  private controlMessages = 0;
  private controlBytes = 0;
  private lastFrameAt?: number;
  private freezeCount = 0;
  private totalFreezeDurationMs = 0;
  private maxFreezeDurationMs = 0;

  constructor(
    private readonly callbacks: Callbacks,
    private profile: StreamProfile,
    private readonly video?: HTMLVideoElement,
  ) {}

  connect() {
    this.stopped = false;
    this.callbacks.onState("connecting");
    performanceStore.update({ backend: "scrcpy-webcodecs", connectionState: "connecting" });
    this.openSockets();
  }

  disconnect() {
    this.stopped = true;
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.closeSockets();
    if (this.decoder && this.decoder.state !== "closed") this.decoder.close();
    this.decoder = undefined;
    this.destroyMse();
    this.callbacks.onState("disconnected");
  }

  reconnect() {
    this.closeSockets();
    if (this.decoder && this.decoder.state !== "closed") this.decoder.close();
    this.decoder = undefined;
    this.destroyMse();
    this.configured = false;
    this.configurePromise = undefined;
    this.pendingKeyFrame = undefined;
    this.codecConfig = undefined;
    this.awaitingKeyFrame = true;
    this.connect();
  }

  setProfile(profile: StreamProfile) {
    if (profile === this.profile) return;
    this.profile = profile;
    this.reconnect();
  }

  sendMouse(x: number, y: number, buttons: number, essential = false) {
    const id = -1;
    const wasDown = this.activeTouches.has(id);
    const down = buttons !== 0;
    const action = down ? (wasDown ? 2 : 0) : 1;
    this.sendTouchPacket(id, x, y, action, down ? 1 : 0, buttons ? 1 : 0, essential);
    if (down) this.activeTouches.add(id); else this.activeTouches.delete(id);
  }

  sendTouch(touches: DirectTouch[], essential = false) {
    for (const touch of touches) {
      const wasDown = this.activeTouches.has(touch.identifier);
      const down = touch.pressure > 0;
      const action = down ? (wasDown ? 2 : 0) : 1;
      this.sendTouchPacket(
        touch.identifier, touch.x, touch.y, action,
        Math.max(0, Math.min(1, touch.pressure / 0x7fff)), 0, essential,
      );
      if (down) this.activeTouches.add(touch.identifier); else this.activeTouches.delete(touch.identifier);
    }
  }

  sendKey(key: string, eventType: 0 | 1 | 2 = 2) {
    if (!directControl.canHumanControl()) return;
    const code = androidKeyCode(key);
    if (code === undefined) return;
    if (eventType === 2) {
      this.sendKeyPacket(code, 0);
      this.sendKeyPacket(code, 1);
    } else {
      this.sendKeyPacket(code, eventType);
    }
  }

  private openSockets() {
    if (!ScrcpyWebCodecsClient.supported()) {
      this.callbacks.onError(new Error("This browser cannot decode the scrcpy H.264 stream"));
      return;
    }
    if (!ScrcpyWebCodecsClient.webCodecsSupported()) {
      if (!this.video) {
        this.callbacks.onError(new Error("The HTTP H.264 player is unavailable"));
        return;
      }
      this.initializeMse(this.video);
    }
    const video = new WebSocket(socketUrl("/scrcpy/ws/video", this.profile));
    video.binaryType = "arraybuffer";
    this.videoSocket = video;
    video.onmessage = (event) => this.receiveVideo(event.data);
    video.onerror = () => this.callbacks.onError(new Error("scrcpy video relay failed"));
    video.onclose = () => this.scheduleReconnect();

    const control = new WebSocket(socketUrl("/scrcpy/ws/control", this.profile));
    control.binaryType = "arraybuffer";
    this.controlSocket = control;
    control.onopen = () => performanceStore.update({ dataChannelState: "open" });
    control.onclose = () => performanceStore.update({ dataChannelState: "closed" });
    control.onerror = () => this.callbacks.onError(new Error("scrcpy control relay failed"));
  }

  private closeSockets() {
    for (const socket of [this.videoSocket, this.controlSocket]) {
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
    }
    this.videoSocket = this.controlSocket = undefined;
  }

  private scheduleReconnect() {
    if (this.stopped || this.reconnectTimer) return;
    performanceStore.update({
      connectionState: "reconnecting",
      reconnectCount: performanceStore.getSnapshot().reconnectCount + 1,
    });
    this.callbacks.onState("reconnecting");
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.reconnect();
    }, 1000);
  }

  private receiveVideo(data: string | ArrayBuffer) {
    if (typeof data === "string") {
      const metadata = JSON.parse(data);
      if (metadata.type === "metadata") {
        performanceStore.update({
          backend: "scrcpy-webcodecs",
          senderCodec: metadata.codec === "h264" ? "video/H264" : metadata.codec,
          senderEncoder: metadata.encoder && metadata.encoder !== "unknown" ? metadata.encoder : undefined,
          senderEncoderType: metadata.encoderType,
          configuredFps: metadata.configuredFps,
          width: metadata.width || undefined,
          height: metadata.height || undefined,
          icePath: "WebSocket · encoded stream relay",
          protocol: "tcp",
        });
      }
      return;
    }
    const packet = new Uint8Array(data);
    if (packet.length < 10) return;
    const flags = packet[0];
    const timestamp = Number(new DataView(data, 1, 8).getBigUint64(0, false));
    const payload = packet.slice(9);
    if (flags & 1) {
      this.codecConfig = payload;
      this.decoderCodec = avcCodecFromAnnexB(payload);
      if (ScrcpyWebCodecsClient.webCodecsSupported()) void this.configureDecoder();
      return;
    }
    const key = Boolean(flags & 2);
    if (this.awaitingKeyFrame && !key) {
      this.droppedFrames += 1;
      this.updateStats();
      return;
    }
    if (!ScrcpyWebCodecsClient.webCodecsSupported()) {
      this.feedMse(timestamp, payload, key);
      return;
    }
    if (!this.configured || !this.decoder || this.decoder.state !== "configured") {
      if (key) this.pendingKeyFrame = { timestamp, payload };
      void this.configureDecoder();
      return;
    }
    this.decodeFrame(timestamp, payload, key);
  }

  private initializeMse(video: HTMLVideoElement) {
    const JMuxer = JMuxerModule as JMuxerConstructor | undefined;
    if (typeof JMuxer !== "function") {
      this.callbacks.onError(new Error("The HTTP H.264 player failed to load"));
      return;
    }
    video.muted = true;
    video.addEventListener("canplay", this.playMseVideo);
    video.addEventListener("playing", this.markMseStreaming);
    this.muxer = new JMuxer({
      node: video,
      mode: "video",
      flushingTime: 0,
      maxDelay: 80,
      clearBuffer: true,
      fps: 30,
      onReady: this.playMseVideo,
      onError: (error) => this.callbacks.onError(this.normalizeMseError(error)),
      onUnsupportedCodec: (codec) => this.callbacks.onError(new Error(`Browser cannot decode Android H.264 codec ${codec}`)),
    });
    performanceStore.update({ decoderImplementation: "Media Source Extensions · native H.264" });
    if ("requestVideoFrameCallback" in video) {
      this.videoFrameCallbackId = video.requestVideoFrameCallback(this.trackMseFrame);
    }
  }

  private destroyMse() {
    const video = this.video;
    this.muxer?.destroy();
    this.muxer = undefined;
    if (video) {
      video.removeEventListener("canplay", this.playMseVideo);
      video.removeEventListener("playing", this.markMseStreaming);
      if (this.videoFrameCallbackId !== undefined && "cancelVideoFrameCallback" in video) {
        video.cancelVideoFrameCallback(this.videoFrameCallbackId);
      }
      video.pause();
      video.removeAttribute("src");
      video.load();
    }
    this.videoFrameCallbackId = undefined;
    this.lastMseTimestamp = undefined;
    this.lastPresentedFrames = 0;
    this.msePlaying = false;
  }

  private readonly playMseVideo = () => {
    void this.video?.play().catch((error) => {
      this.callbacks.onError(new Error(`H.264 playback failed: ${String(error)}`));
    });
  };

  private readonly markMseStreaming = () => {
    performanceStore.update({
      connectionState: "connected",
      decoderImplementation: "Media Source Extensions · native H.264",
    });
    if (this.msePlaying) return;
    this.msePlaying = true;
    this.callbacks.onState("streaming");
  };

  private readonly trackMseFrame: VideoFrameRequestCallback = (_now, metadata) => {
    const presented = metadata.presentedFrames || this.lastPresentedFrames + 1;
    const increment = Math.max(1, presented - this.lastPresentedFrames);
    this.lastPresentedFrames = presented;
    this.decodedFrames += increment;
    this.intervalFrames += increment;
    const quality = this.video?.getVideoPlaybackQuality?.();
    if (quality) this.droppedFrames = Math.max(this.droppedFrames, quality.droppedVideoFrames);
    this.updateFrameTiming();
    this.updateStats();
    this.markMseStreaming();
    if (!this.stopped && this.video && "requestVideoFrameCallback" in this.video) {
      this.videoFrameCallbackId = this.video.requestVideoFrameCallback(this.trackMseFrame);
    }
  };

  private feedMse(timestamp: number, payload: Uint8Array, key: boolean) {
    if (!this.muxer) return;
    if (key) this.awaitingKeyFrame = false;
    const bytes = key && this.codecConfig ? concat(this.codecConfig, payload) : payload;
    const previous = this.lastMseTimestamp;
    this.lastMseTimestamp = timestamp;
    const duration = previous === undefined
      ? 33
      : Math.max(1, Math.min(250, Math.round((timestamp - previous) / 1000)));
    try {
      this.muxer.feed({ video: bytes, duration, isLastVideoFrameComplete: true });
    } catch (error) {
      this.callbacks.onError(this.normalizeMseError(error));
    }
  }

  private normalizeMseError(error: unknown) {
    if (error instanceof Error) return error;
    if (error && typeof error === "object" && "name" in error) {
      return new Error(`H.264 playback error: ${String(error.name)}`);
    }
    return new Error(`H.264 playback error: ${String(error)}`);
  }

  private updateFrameTiming() {
    const outputAt = performance.now();
    if (this.lastFrameAt !== undefined) {
      const gap = outputAt - this.lastFrameAt;
      if (gap > 500) {
        this.freezeCount += 1;
        this.totalFreezeDurationMs += gap;
        this.maxFreezeDurationMs = Math.max(this.maxFreezeDurationMs, gap);
      }
    }
    this.lastFrameAt = outputAt;
  }

  private decodeFrame(timestamp: number, payload: Uint8Array, key: boolean) {
    if (!this.decoder || this.decoder.state !== "configured") return;
    if (this.decoder.decodeQueueSize > 2 && !key) {
      this.droppedFrames += 1;
      this.awaitingKeyFrame = true;
      this.submitted.clear();
      this.decoder.reset();
      this.updateStats();
      return;
    }
    const bytes = key && this.codecConfig ? concat(this.codecConfig, payload) : payload;
    if (key) this.awaitingKeyFrame = false;
    this.submitted.set(timestamp, performance.now());
    try {
      this.decoder.decode(new EncodedVideoChunk({ type: key ? "key" : "delta", timestamp, data: bytes }));
    } catch (error) {
      this.droppedFrames += 1;
      const normalized = error instanceof Error ? error : new Error(String(error));
      this.awaitingKeyFrame = true;
      if (!normalized.message.includes("key frame is required")) {
        this.callbacks.onError(normalized);
      }
    }
  }

  private configureDecoder() {
    if (this.configured) return Promise.resolve();
    if (this.configurePromise) return this.configurePromise;
    this.configurePromise = this.configureDecoderOnce().finally(() => { this.configurePromise = undefined; });
    return this.configurePromise;
  }

  private async configureDecoderOnce() {
    if (this.decoder?.state === "closed") this.decoder = undefined;
    if (!this.decoder) {
      this.decoder = new VideoDecoder({
        output: (frame) => {
          this.updateFrameTiming();
          const submitted = this.submitted.get(frame.timestamp);
          if (submitted !== undefined) {
            this.decodeTotalMs += performance.now() - submitted;
            this.submitted.delete(frame.timestamp);
          }
          this.decodedFrames += 1;
          this.intervalFrames += 1;
          this.callbacks.onFrame(frame);
          this.updateStats();
          if (this.decodedFrames === 1) {
            performanceStore.update({ connectionState: "connected" });
            this.callbacks.onState("streaming");
          }
        },
        error: (error) => {
          this.configured = false;
          this.decoder = undefined;
          this.awaitingKeyFrame = true;
          this.submitted.clear();
          this.callbacks.onError(error);
        },
      });
    }
    try {
      const preferred: VideoDecoderConfig = {
        codec: this.decoderCodec,
        optimizeForLatency: true,
        hardwareAcceleration: "prefer-hardware",
      };
      const hardware = await VideoDecoder.isConfigSupported(preferred);
      const selected: VideoDecoderConfig = hardware.supported
        ? preferred
        : { ...preferred, hardwareAcceleration: "no-preference" };
      const supported = hardware.supported ? hardware : await VideoDecoder.isConfigSupported(selected);
      if (!supported.supported) throw new Error(`Browser cannot decode the Android H.264 profile ${this.decoderCodec}`);
      this.decoder.configure(selected);
      this.configured = true;
      performanceStore.update({
        decoderImplementation: hardware.supported ? "WebCodecs · hardware preferred" : "WebCodecs · automatic/software fallback",
      });
      const pending = this.pendingKeyFrame;
      this.pendingKeyFrame = undefined;
      if (pending) this.decodeFrame(pending.timestamp, pending.payload, true);
    } catch (error) {
      this.callbacks.onError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  private updateStats() {
    const now = performance.now();
    const elapsed = now - this.intervalStarted;
    const total = this.decodedFrames + this.droppedFrames;
    const values: Parameters<typeof performanceStore.update>[0] = {
      framesDecoded: this.decodedFrames,
      framesDropped: this.droppedFrames,
      droppedFramePercent: total ? Math.round(this.droppedFrames / total * 10_000) / 100 : 0,
      averageDecodeTimeMs: this.decodedFrames ? Math.round(this.decodeTotalMs / this.decodedFrames * 100) / 100 : 0,
      decoderQueueSize: this.decoder?.decodeQueueSize,
      freezeCount: this.freezeCount,
      totalFreezeDurationMs: Math.round(this.totalFreezeDurationMs * 10) / 10,
      maxFreezeDurationMs: Math.round(this.maxFreezeDurationMs * 10) / 10,
    };
    if (elapsed >= 1000) {
      values.fps = Math.round(this.intervalFrames * 1000 / elapsed * 10) / 10;
      this.intervalFrames = 0;
      this.intervalStarted = now;
    }
    performanceStore.update(values);
  }

  private sendTouchPacket(id: number, x: number, y: number, action: number, pressure: number, buttons: number, essential: boolean) {
    const stats = performanceStore.getSnapshot();
    const screenWidth = stats.width || 1280;
    const screenHeight = stats.height || 720;
    const packet = new ArrayBuffer(32);
    const view = new DataView(packet);
    view.setUint8(0, 2);
    view.setUint8(1, action);
    view.setBigUint64(2, id < 0 ? BigInt.asUintN(64, BigInt(id)) : BigInt(id), false);
    setU32(view, 10, x);
    setU32(view, 14, y);
    setU16(view, 18, screenWidth);
    setU16(view, 20, screenHeight);
    setU16(view, 22, Math.round(pressure * 65535));
    setU32(view, 24, buttons);
    setU32(view, 28, buttons);
    this.sendControl(packet, essential);
  }

  private sendKeyPacket(code: number, action: number) {
    const packet = new ArrayBuffer(14);
    const view = new DataView(packet);
    view.setUint8(0, 0);
    view.setUint8(1, action);
    setU32(view, 2, code);
    setU32(view, 6, 0);
    setU32(view, 10, 0);
    this.sendControl(packet, true);
  }

  sendScroll(x: number, y: number, deltaX: number, deltaY: number) {
    if (!directControl.canHumanControl()) return;
    const stats = performanceStore.getSnapshot();
    const packet = new ArrayBuffer(21);
    const view = new DataView(packet);
    view.setUint8(0, 3);
    setU32(view, 1, x);
    setU32(view, 5, y);
    setU16(view, 9, stats.width || 1280);
    setU16(view, 11, stats.height || 720);
    view.setInt16(13, deltaX === 0 ? 0 : deltaX > 0 ? -32767 : 32767, false);
    view.setInt16(15, deltaY === 0 ? 0 : deltaY > 0 ? -32767 : 32767, false);
    setU32(view, 17, 0);
    this.sendControl(packet, true);
  }

  rotate() {
    if (!directControl.canHumanControl()) return;
    const packet = new ArrayBuffer(1);
    new DataView(packet).setUint8(0, 11);
    this.sendControl(packet, true);
  }

  private sendControl(packet: ArrayBuffer, essential: boolean) {
    const socket = this.controlSocket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    if (!essential && socket.bufferedAmount > 32 * 1024) return;
    socket.send(packet);
    this.controlMessages += 1;
    this.controlBytes += packet.byteLength;
    performanceStore.update({
      dataMessagesSent: this.controlMessages,
      dataBytesSent: this.controlBytes,
      dataBufferedAmount: socket.bufferedAmount,
    });
  }
}
